import { createApi } from '@reduxjs/toolkit/query/react'
import { User } from '../../models/User'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { removeEmptyItems } from '../../common/util'
import { ConsoleUser } from '../../models/ConsoleUser'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { PendingInvitationFilterModel } from '../../views/ManageTeam/PendingInvitations/PendingInvitationsFilter'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AUTH_API_BASE_URL!

export interface CsvInviteUser {
  email: string
  firstName: string
  lastName: string
  role: string
  roleIds: string[]
}

export interface CheckedCsvInviteUser extends CsvInviteUser {
  errors: string[]
  warnings: string[]
  inviteStatus: 'already_member' | 'already_invited' | null
}

export interface UserInvitationResult extends CsvInviteUser {
  status: string
  isError: boolean
}

interface BulkInviteRequest extends BaseRequest {
  users: CsvInviteUser[]
}

export interface BulkInviteVerificationResponse {
  users: CheckedCsvInviteUser[]
  generalErrors: string[]
}

export interface BulkInviteResponse {
  users: UserInvitationResult[]
  generalErrors: string[]
}

interface InviteUserRequest extends BaseRequest {
  userData: ConsoleUser
}

interface ResendInviteBody {
  ccMe: boolean
}

interface ResendInviteRequest extends BaseRequest {
  inviteId: string
  body: ResendInviteBody
}

interface InviteDeleteRequest extends BaseRequest {
  inviteId: string
}

export interface InviteResponse {
  id: string
  createdAt: string
  expiresAt?: string
  usedAt?: string
  messageState?: string
  state: string
  user: ConsoleUser
}

export interface OAuth2AuthorizeResponse {
  redirectUri: string
}

export interface LoginToAcademyResponse {
  loginUrl: string
}

export interface OAuth2AuthorizeRequestBody {
  client_id: string
  redirect_uri: string
  response_type: string
  scope: string
  state: string | null
  code_challenge_method?: string
  code_challenge?: string
}

export interface OAuth2AuthorizeRequest extends BaseRequest {
  body: OAuth2AuthorizeRequestBody
}

export type OAuth2AppType = 'dev' | 'internal' | 'external'

export type OAuth2AppCategory = 'public_api' | 'mcp'

export interface ShortOAuth2App {
  id: string
  type: OAuth2AppType
  name: string
  clientId: string
  imageUrl?: string | null
  description: Record<string, unknown>
  shortDescription?: string | null
  enabled: boolean
  category?: string | null
}

export interface GetOAuth2AppByIdRequest extends BaseRequest {
  clientId: string
}

export interface OAuth2AppPermissions {
  account: boolean
  systemRoles: Record<string, boolean>
  accountRoles: Record<string, boolean>
}

export interface OAuth2App {
  id: string
  accountId: string | null
  type: OAuth2AppType
  clientId: string
  name: string
  imageUrl: string | null
  description: Record<string, unknown>
  shortDescription?: string | null
  scopes: string[]
  redirectUris: string[]
  tokenEndpointAuthMethod: string
  permissions: OAuth2AppPermissions
}

export interface ListOAuth2AppsRequest extends BaseRequest {
  accountId: string
}

export interface GetOAuth2AppRequest extends BaseRequest {
  accountId: string
  clientId: string
}

export interface OAuth2AppPermissionsBody {
  account?: boolean
  systemRoles?: Record<string, boolean>
  accountRoles?: Record<string, boolean>
}

export interface SetOAuth2AppPermissionsRequest extends BaseRequest {
  accountId: string
  clientId: string
  body: OAuth2AppPermissionsBody
}

export interface OAuth2AppCredentials {
  clientId: string
  clientSecret: string
}

export interface CreateOAuth2AppPayload {
  name: string
  category: OAuth2AppCategory
  imageUrl?: string | null
  description?: Record<string, unknown>
  shortDescription: string
  redirectUris: string[]
  scopes?: string[]
  tokenEndpointAuthMethod?: string
}

export interface CreateOAuth2AppRequest extends BaseRequest {
  accountId: string
  payload: CreateOAuth2AppPayload
}

export interface UpdateOAuth2AppPayload {
  name?: string
  imageUrl?: string | null
  description?: Record<string, unknown>
  shortDescription?: string
  redirectUris?: string[]
  scopes?: string[]
}

export interface UpdateOAuth2AppRequest extends BaseRequest {
  accountId: string
  clientId: string
  payload: UpdateOAuth2AppPayload
}

export interface RotateOAuth2AppSecretRequest extends BaseRequest {
  accountId: string
  clientId: string
}

export interface DeleteOAuth2AppRequest extends BaseRequest {
  accountId: string
  clientId: string
}

export interface GetInvitesRequest extends BasePageRequest {
  filter: PendingInvitationFilterModel
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['invites', 'apps', 'app-scopes'],
  endpoints: (builder) => ({
    getInvites: builder.query<BasePageResponse<InviteResponse[]>, GetInvitesRequest>({
      query: (request) => {
        return {
          url: `/v2/accounts/${request.accountId}/invite/filter`,
          method: 'POST',
          params: { page: request.pageRequest.page, size: request.pageRequest.size },
          body: {
            filter: request.filter,
          },
        }
      },
      providesTags: ['invites'],
    }),
    inviteUser: builder.mutation<BaseResponse<User>, InviteUserRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite`,
        method: 'POST',
        body: removeEmptyItems(request.userData),
      }),
      invalidatesTags: ['invites'],
    }),
    bulkInviteVerification: builder.mutation<BulkInviteVerificationResponse, BulkInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/bulk-verify`,
        method: 'POST',
        body: { users: request.users },
      }),
    }),
    bulkInvite: builder.mutation<BulkInviteResponse, BulkInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/bulk-invite`,
        method: 'POST',
        body: { users: request.users },
      }),
      invalidatesTags: ['invites'],
    }),
    resendInvite: builder.mutation<BaseResponse<User>, ResendInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/${request.inviteId}/resend`,
        method: 'POST',
        body: request.body,
      }),
      invalidatesTags: ['invites'],
    }),
    deleteInvite: builder.mutation<BaseResponse<User>, InviteDeleteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/${request.inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['invites'],
    }),
    oauth2Authorize: builder.mutation<BaseResponse<OAuth2AuthorizeResponse>, OAuth2AuthorizeRequest>({
      query: (request) => ({
        url: `/v1/accounts/${request.accountId}/oauth2/authorize`,
        method: 'POST',
        body: request.body,
      }),
    }),
    listOAuth2Apps: builder.query<BaseResponse<ShortOAuth2App[]>, ListOAuth2AppsRequest>({
      query: ({ accountId }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps`,
        method: 'GET',
      }),
      providesTags: ['apps'],
    }),
    getOAuth2AppByClientId: builder.query<OAuth2App, GetOAuth2AppRequest>({
      query: ({ accountId, clientId }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<OAuth2App>) => response.data,
      providesTags: ['apps'],
    }),
    setOAuth2AppPermissions: builder.mutation<void, SetOAuth2AppPermissionsRequest>({
      query: ({ accountId, clientId, body }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}/permissions`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['apps'],
    }),
    loginToAcademy: builder.mutation<BaseResponse<LoginToAcademyResponse>, null>({
      query: () => ({
        url: `/v2/console/login-to-academy`,
        method: 'POST',
      }),
    }),
    getAccountAppScopes: builder.query<{ data: { scopes: string[] } }, { accountId: string; clientId: string }>({
      query: ({ accountId, clientId }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}/scopes`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { clientId }) => [{ type: 'app-scopes', id: clientId }],
    }),
    setAccountAppScopes: builder.mutation<void, { accountId: string; clientId: string; scopes: string[] }>({
      query: ({ accountId, clientId, scopes }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}/scopes`,
        method: 'PUT',
        body: { scopes },
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-scopes', id: clientId }],
    }),
    createOAuth2App: builder.mutation<OAuth2AppCredentials, CreateOAuth2AppRequest>({
      query: ({ accountId, payload }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['apps'],
    }),
    updateOAuth2App: builder.mutation<void, UpdateOAuth2AppRequest>({
      query: ({ accountId, clientId, payload }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['apps'],
    }),
    rotateOAuth2AppSecret: builder.mutation<OAuth2AppCredentials, RotateOAuth2AppSecretRequest>({
      query: ({ accountId, clientId }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}/secret/rotate`,
        method: 'POST',
      }),
      invalidatesTags: ['apps'],
    }),
    deleteOAuth2App: builder.mutation<void, DeleteOAuth2AppRequest>({
      query: ({ accountId, clientId }) => ({
        url: `/v1/accounts/${accountId}/oauth2/apps/${clientId}`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useInviteUserMutation,
  useGetInvitesQuery,
  useLazyGetInvitesQuery,
  useDeleteInviteMutation,
  useResendInviteMutation,
  useBulkInviteVerificationMutation,
  useBulkInviteMutation,
  useOauth2AuthorizeMutation,
  useListOAuth2AppsQuery,
  useLazyListOAuth2AppsQuery,
  useGetOAuth2AppByClientIdQuery,
  useLazyGetOAuth2AppByClientIdQuery,
  useSetOAuth2AppPermissionsMutation,
  useLoginToAcademyMutation,
  useGetAccountAppScopesQuery,
  useSetAccountAppScopesMutation,
  useCreateOAuth2AppMutation,
  useUpdateOAuth2AppMutation,
  useRotateOAuth2AppSecretMutation,
  useDeleteOAuth2AppMutation,
} = authApi
